/**
 * The Battle Plan (web Home → Battle Plan tab): every live job ranked by how much it needs you now, and the next two
 * weeks laid out by day — which jobs are on, which are due, how many tasks are dated. Pure; computed, never a manual
 * priority (CLAUDE.md).
 *
 * A job's score: overdue (+120) or due soon (+ up to 80 over the last 8 days), carried tasks (+6 each, max 30),
 * critical / high open tasks (+15 / +6 each, max 45), on the schedule today (+25) or in the next 3 days (+10),
 * open money tasks (+10). `reasons` says why in words, most important first.
 */
import { isProjectLive } from './projectstatus.js';
import { smartSortTasks } from './priority.js';

const DAY_MS = 86400000;
const dayNum = (d) => Math.floor(Date.parse(`${d}T12:00:00Z`) / DAY_MS);
const addDays = (d, n) => new Date((dayNum(d) + n) * DAY_MS).toISOString().slice(0, 10);
const open = (t) => t.status !== 'done' && !t.deletedAt;
const money = (t) => (t.moneyEarned || 0) + (t.actualValue || 0) > 0;

export function jobBoard({ projects = [], tasks = [], blocks = [], today, horizonDays = 14 }) {
  const byProject = new Map();
  for (const t of tasks) if (t.projectId) (byProject.get(t.projectId) || byProject.set(t.projectId, []).get(t.projectId)).push(t);
  const onDays = new Map();
  for (const b of blocks) if (b.projectId && b.date >= today) (onDays.get(b.projectId) || onDays.set(b.projectId, new Set()).get(b.projectId)).add(b.date);

  const rows = projects.filter(isProjectLive).map((p) => {
    const all = byProject.get(p.id) || [];
    const top = all.filter((t) => !t.parentId);
    const openT = all.filter(open);
    const doneN = all.length - openT.length;
    const carried = openT.filter((t) => t.scheduledDate && t.scheduledDate < today);
    const crit = openT.filter((t) => t.priority === 'critical').length;
    const high = openT.filter((t) => t.priority === 'high').length;
    const days = new Set(onDays.get(p.id) || []);
    for (const t of openT) if (t.scheduledDate && t.scheduledDate >= today) days.add(t.scheduledDate);
    const nextOn = [...days].sort()[0] || null;
    const daysLeft = p.endDate ? dayNum(p.endDate) - dayNum(today) : null;
    const estMin = openT.reduce((a, t) => a + (Number(t.estimatedMinutes) || 0), 0);
    // Next: today's / carried first, then dated soonest, then the smart order of the rest (top-level before subtasks).
    const dated = openT.filter((t) => t.scheduledDate).sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
    const next = dated.find((t) => t.scheduledDate <= today) || dated[0] || smartSortTasks(openT.filter((t) => !t.parentId), { today })[0] || smartSortTasks(openT, { today })[0] || null;

    let score = 0; const reasons = [];
    if (daysLeft != null && daysLeft < 0) { score += 120; reasons.push(`Overdue ${-daysLeft}d`); }
    else if (daysLeft != null && daysLeft <= 7) { score += 80 - daysLeft * 10; reasons.push(daysLeft === 0 ? 'Due today' : daysLeft === 1 ? 'Due tomorrow' : `Due in ${daysLeft} days`); }
    if (crit) { score += Math.min(45, crit * 15); reasons.push(`${crit} critical`); }
    if (carried.length) { score += Math.min(30, carried.length * 6); reasons.push(`${carried.length} carried`); }
    if (nextOn === today) { score += 25; reasons.push('On today'); }
    else if (nextOn && dayNum(nextOn) - dayNum(today) <= 3) { score += 10; reasons.push(`On ${nextOn === addDays(today, 1) ? 'tomorrow' : 'in ' + (dayNum(nextOn) - dayNum(today)) + ' days'}`); }
    if (high) { score += Math.min(30, high * 6); if (!crit) reasons.push(`${high} high`); }
    if (openT.some(money)) { score += 10; reasons.push('Money on it'); }
    if (!openT.length && top.length) reasons.push('All tasks done');
    if (!all.length) reasons.push('No tasks yet');

    return {
      id: p.id, project: p, score, reasons,
      endDate: p.endDate || null, daysLeft, nextOn,
      open: openT.length, done: doneN, total: all.length, pct: all.length ? Math.round((doneN / all.length) * 100) : 0,
      carried: carried.length, critical: crit, estMin, next,
      onDays: [...days].filter((d) => dayNum(d) - dayNum(today) < horizonDays).sort(),
    };
  });
  rows.sort((a, b) => b.score - a.score || (a.daysLeft ?? 1e9) - (b.daysLeft ?? 1e9) || (a.nextOn || '9').localeCompare(b.nextOn || '9') || b.open - a.open);
  return rows;
}

/** The next `days` days: which jobs are on (blocks / dated tasks), which are due, and how many open tasks are dated. */
export function planTimeline({ board = [], tasks = [], today, days = 14 }) {
  const out = [];
  for (let i = 0; i < days; i++) {
    const date = addDays(today, i);
    out.push({
      date,
      on: board.filter((r) => r.onDays.includes(date)).map((r) => r.id),
      due: board.filter((r) => r.endDate === date).map((r) => r.id),
      tasks: tasks.filter((t) => open(t) && t.scheduledDate === date).length,
    });
  }
  return out;
}

/**
 * The numbers across the top of Projects: live jobs past their end date (or with overdue tasks), open tasks on live
 * jobs, how far ahead the calendar is booked (the last job day from today → weeks), jobs due in the next two weeks.
 */
export function projectsOverview({ projects = [], tasks = [], blocks = [], today }) {
  const live = projects.filter(isProjectLive);
  const liveIds = new Set(live.map((p) => p.id));
  const openT = tasks.filter((t) => open(t) && !t.bucket && liveIds.has(t.projectId));
  const lateTaskJobs = new Set(openT.filter((t) => t.scheduledDate && t.scheduledDate < today).map((t) => t.projectId));
  const overdue = live.filter((p) => (p.endDate && p.endDate < today) || lateTaskJobs.has(p.id));
  const booked = blocks.filter((b) => b.projectId && b.date >= today && liveIds.has(b.projectId)).map((b) => b.date).sort();
  const bookedUntil = booked.length ? booked[booked.length - 1] : null;
  const bookedDays = bookedUntil ? dayNum(bookedUntil) - dayNum(today) + 1 : 0;
  const dueSoon = live.filter((p) => p.endDate && p.endDate >= today && dayNum(p.endDate) - dayNum(today) <= 14);
  return {
    live: live.length, overdue: overdue.map((p) => p.id), openTasks: openT.length,
    bookedUntil, bookedDays, bookedWeeks: Math.round((bookedDays / 7) * 10) / 10, dueSoon: dueSoon.map((p) => p.id),
  };
}
